/* eslint-disable react/no-array-index-key */

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Flex } from "orcs-design-system";
import { useHistory } from "react-router-dom";
import { useScrollToBottom } from "src/hooks/useScrollToBottom";
import {
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useUserPerson } from "src/contexts/global/GlobalContext";
import { getName } from "src/util/person";
import { AIMessage } from "../AIMessage";
import { createHtmlParsingOptions } from "../HTMLElementOverrides";
import AIChatWelcome from "../AIChatWelcome";
import { ToolBadge } from "./ToolBadge";
import { AssistantMessage } from "./AssistantMessage";
import { UserMessage } from "./UserMessage";
import { AIMessageWrapper } from "./AIMessageWrapper";
import { useAIChatV3 } from "./AIChatV3Context";
import { CHAT_PHASE } from "./hooks/useAIChatPhase";

const PHASE_LABELS = {
  [CHAT_PHASE.CONNECTING]: "Connecting... ",
  [CHAT_PHASE.THINKING]: "Thinking... ",
};

/**
 * v3 message list — renders chat messages, tool badges, and phase indicators.
 * Consumes AIChatV3Context (chatPhase instead of boolean flags).
 */
export const AIChatV3Messages = ({ htmlElementOverrides }) => {
  const workspace = useWorkspace();
  const history = useHistory();
  const { enableAIDashboards } = useWorkspaceFeatureFlags();
  const { user } = useAuth0();
  const userPerson = useUserPerson();

  const {
    canRetryOrEdit,
    editingMessageId,
    messages,
    previousMessages,
    chatPhase,
    isComplete,
    setUserHasScrolledUp,
    userHasScrolledUp,
    chatId,
    setQuestion,
    question,
    onRetry,
    onEditLastMessage,
    onSendQuestion,
    toolsUsedCount,
    onSubmitMessageFeedback,
    selectedAgent,
  } = useAIChatV3();

  // -- Prompt selection (two-tick send to avoid stale closure) --
  const [pendingSend, setPendingSend] = React.useState(false);

  useEffect(() => {
    if (pendingSend && question) {
      onSendQuestion();
      setPendingSend(false);
    }
  }, [pendingSend, question, onSendQuestion]);

  const handlePromptSelect = React.useCallback(
    (prompt) => {
      setQuestion(prompt);
      setPendingSend(true);
    },
    [setQuestion]
  );

  // -- Auto-scroll --
  const { scrollContainerRef, scrollToBottom, handleScroll } =
    useScrollToBottom({
      threshold: 100,
      setUserHasScrolledUp,
      userHasScrolledUp,
    });

  useEffect(() => {
    if (messages.length > 0) {
      const hasNewContent =
        JSON.stringify(messages) !== JSON.stringify(previousMessages);
      if (hasNewContent && !userHasScrolledUp) {
        requestAnimationFrame(scrollToBottom);
      }
    }
  }, [messages, previousMessages, userHasScrolledUp, scrollToBottom]);

  // -- Shared HTML parsing options --
  const makeHtmlParsingOptions = (isStreaming) =>
    createHtmlParsingOptions(
      workspace.id,
      chatId,
      history,
      htmlElementOverrides,
      enableAIDashboards,
      isStreaming
    );

  // -- Phase indicator label --
  const phaseLabel = PHASE_LABELS[chatPhase] ?? null;

  return (
    <Box
      borderRadius={2}
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <Box
        ref={scrollContainerRef}
        flex="1 1 auto"
        display="flex"
        flexDirection="column"
        width="100%"
        overflowY="auto"
        minHeight={0}
        padding="8px"
        onScroll={handleScroll}
      >
        {/* Welcome screen */}
        {messages.length === 0 &&
          isComplete &&
          (() => {
            const personName = userPerson ? getName(userPerson) : null;
            const userName =
              personName || user?.given_name || user?.nickname || "there";
            return (
              <AIChatWelcome
                userName={userName}
                onPromptSelect={handlePromptSelect}
                showSuggestions={!question}
              />
            );
          })()}

        {/* Messages */}
        {messages
          .map((message, idx) => {
            const elements = [];

            // Tool count badge — render above the last streaming assistant message
            if (
              message.role === "assistant" &&
              toolsUsedCount > 0 &&
              chatPhase !== CHAT_PHASE.IDLE &&
              chatPhase !== CHAT_PHASE.DONE &&
              !messages.slice(idx + 1).some((m) => m.role === "assistant")
            ) {
              elements.push(
                <Flex
                  flexDirection="column"
                  justifyContent="flex-start"
                  mt="4px"
                  mb="4px"
                  gap="8px"
                  key="tools-used-hint"
                >
                  <AIMessageWrapper>
                    <ToolBadge
                      message={{ status: "pending", args: { name: "" } }}
                      allToolsCalled={Array.from(
                        { length: toolsUsedCount },
                        () => ({})
                      )}
                    />
                  </AIMessageWrapper>
                </Flex>
              );
            }

            if (message.role === "assistant") {
              elements.push(
                <AssistantMessage
                  key={`assistant-${idx}`}
                  message={message}
                  messages={messages}
                  idx={idx}
                  isComplete={isComplete}
                  selectedAgent={selectedAgent}
                  makeHtmlParsingOptions={makeHtmlParsingOptions}
                  handlePromptSelect={handlePromptSelect}
                  onSubmitMessageFeedback={onSubmitMessageFeedback}
                />
              );
            }
            if (message.role === "user") {
              elements.push(
                <UserMessage
                  key={`user-${idx}`}
                  message={message}
                  messages={messages}
                  idx={idx}
                  canRetryOrEdit={canRetryOrEdit}
                  editingMessageId={editingMessageId}
                  onRetry={onRetry}
                  onEditLastMessage={onEditLastMessage}
                  makeHtmlParsingOptions={makeHtmlParsingOptions}
                />
              );
            }
            return elements;
          })
          .flat()
          .filter(Boolean)}

        {/* Tool count badge — shown before any assistant message exists */}
        {toolsUsedCount > 0 &&
          chatPhase !== CHAT_PHASE.IDLE &&
          chatPhase !== CHAT_PHASE.DONE &&
          !messages.some((m) => m.role === "assistant") && (
            <Flex
              flexDirection="column"
              justifyContent="flex-start"
              mt="4px"
              mb="4px"
              gap="8px"
              key="tools-used-hint-standalone"
            >
              <AIMessageWrapper>
                <ToolBadge
                  message={{ status: "pending", args: { name: "" } }}
                  allToolsCalled={Array.from(
                    { length: toolsUsedCount },
                    () => ({})
                  )}
                />
              </AIMessageWrapper>
            </Flex>
          )}

        {/* Phase indicator */}
        {phaseLabel && (
          <Flex
            flexDirection="column"
            justifyContent="flex-start"
            mt="4px"
            mb="4px"
            gap="8px"
            key="phase-indicator"
          >
            <AIMessageWrapper>
              <AIMessage
                complete={false}
                content={phaseLabel}
                options={makeHtmlParsingOptions(false)}
                showAIWarning={false}
              />
            </AIMessageWrapper>
          </Flex>
        )}
      </Box>
    </Box>
  );
};

AIChatV3Messages.propTypes = {
  htmlElementOverrides: PropTypes.any,
};
